import React, { HTMLAttributeAnchorTarget, ReactNode } from 'react';
import { DOMProps, type RouterOptions } from '@react-types/shared';
import { genericForwardRef, genericMemo } from '@direct-frontend/stdlib';

import { TestProps } from '../../internal/types/testTypes';
import { cn } from '../../lib/css/className/className';
import { TextRenderFunction } from '../Text';
import { Cell, CellBaseProps, CellWithColorProps } from '../Cell';
import { StackAlignItems, StackJustifyContent, VStack } from '../Stack';
import { TextSize } from '../Text/types';
import { TextProps as BaseTextProps } from '../Text';
import { CellSizeContext } from '../Table/context';
import { HelpProps } from '../Help';

import { TextCellContent } from './TextCellContent';

import './TextCell.css';

const cls = cn('TextCell');

export interface TextCellBaseProps extends TestProps, DOMProps {
    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Подпись к ячейке - строка или компонент
     */
    caption?: string | TextRenderFunction;

    /**
     * Ссылка в подписи к ячейке
     */
    captionHref?: string;

    /**
     * Опции роутера для перехода по `captionHref`.
     * Передаются вторым аргументом в `navigate` из `RouterProvider`.
     * До расширения интерфейса `RouterConfig` в сервисе тип равен `never`.
     */
    captionRouterOptions?: RouterOptions;

    /**
     * Обработчик клика на подпись к ячейке
     */
    onCaptionClick?: () => void;

    /**
     * Основной текст ячейки - строка или компонент
     */
    title: string | TextRenderFunction;

    /**
     * Ссылка в основном тексте ячейки
     */
    titleHref?: string;

    /**
     * Опции роутера для перехода по `titleHref`.
     * Передаются вторым аргументом в `navigate` из `RouterProvider`.
     * До расширения интерфейса `RouterConfig` в сервисе тип равен `never`.
     */
    titleRouterOptions?: RouterOptions;

    /**
     * Обработчик клика на ссылку в основном тексте ячейки
     */
    titleHrefTarget?: HTMLAttributeAnchorTarget;

    /**
     * Признак обрезания текста при переполнении контейнера.
     */
    isTruncated?: boolean;

    /**
     * Обработчик клика на текст ячейки
     */
    onTitleClick?: () => void;
    /**
     * Компонент со всплывающей подсказкой при клике на знак вопроса.
     * При передаче данного параметра рядом с текстом рисуется иконка `Help` соответствующего размера.
     * @param helpProps - параметры для передадчи во всплывающую подсказку (управляют размером, цветом и выравниванием)
     */
    help?: (helpProps: HelpProps) => ReactNode;
    /**
     * Подсвечена ли ячейка(меняет цвет бекграунда)
     */
    isHighlighted?: boolean;
    /**
     * Выравнивание содержимого ячейки по вертикали
     */
    verticalAlign?: 'top' | 'center' | 'bottom';

    /**
     * Цвет текста заголовка и подписи
     */
    textColor?: BaseTextProps['color'];
}

export type TextCellProps = TextCellBaseProps &
    (Omit<CellBaseProps, 'children' | 'type'> | Omit<CellWithColorProps, 'children' | 'type'>);

/**
 * Описание размеров для размера ячейки
 */
type TextCellSize = {
    /**
     * Размер текста подписи к ячейке
     */
    captionSize: TextSize;
    /**
     * Размер основного текста
     */
    titleSize: TextSize;
};

type TextCellSizeConfigProps = Required<TextCellProps>['size'];

const TextCellSizeConfig: Record<TextCellSizeConfigProps, TextCellSize> = {
    s: {
        captionSize: 'xs',
        titleSize: 'xs',
    },
    m: {
        captionSize: 'xs',
        titleSize: 's',
    },
    l: {
        captionSize: 'xs',
        titleSize: 'mc',
    },
};

/**
 * Компонент для отображения простой текстовой ячейки таблицы, содержащей текст и подпись
 */
export const TextCell = genericMemo(
    genericForwardRef<HTMLDivElement, TextCellProps>(function TextCell({ className, ...props }, ref) {
        const {
            caption,
            captionHref,
            captionRouterOptions,
            onCaptionClick,
            title,
            titleHref,
            titleRouterOptions,
            titleHrefTarget,
            onTitleClick,
            help,
            isTruncated,
            isHighlighted,
            verticalAlign,
            textColor,
            ...cellProps
        } = props;
        const tableSize = React.useContext(CellSizeContext);

        const { align, size = 'm' } = cellProps;

        const cellSize = tableSize ?? size;

        const { captionSize, titleSize } = TextCellSizeConfig[cellSize];

        let contentAlign: StackAlignItems;
        switch (align) {
            case 'center':
                contentAlign = 'center';
                break;
            case 'right':
                contentAlign = 'flex-end';
                break;
            default:
                contentAlign = 'flex-start';
        }

        let contentVerticalAlign: StackJustifyContent;
        switch (verticalAlign) {
            case 'center':
                contentVerticalAlign = 'center';
                break;
            case 'bottom':
                contentVerticalAlign = 'flex-end';
                break;
            default:
                contentVerticalAlign = 'flex-start';
        }

        return (
            <Cell
                className={cls({ truncated: isTruncated }, [className])}
                {...cellProps}
                ref={ref}
                size={cellSize}
                isHighlighted={isHighlighted}
            >
                <VStack
                    className={cls('textContent')}
                    alignItems={contentAlign}
                    justifyContent={contentVerticalAlign}
                    gap="4"
                >
                    <TextCellContent
                        align={align}
                        href={titleHref}
                        target={titleHrefTarget}
                        routerOptions={titleRouterOptions}
                        content={title}
                        size={titleSize}
                        color={textColor}
                        textHandler={onTitleClick}
                        isTruncated={isTruncated}
                        help={help}
                    />
                    {caption && (
                        <TextCellContent
                            align={align}
                            href={captionHref}
                            routerOptions={captionRouterOptions}
                            isTitle={false}
                            content={caption}
                            size={captionSize}
                            color={textColor}
                            isTruncated={isTruncated}
                            textHandler={onCaptionClick}
                        />
                    )}
                </VStack>
            </Cell>
        );
    }),
);
